// src/context/ToastContext.jsx
import React, { createContext, useState, useCallback, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

export const ToastContext = createContext(null);

const slideIn = keyframes`
  from { transform: translateX(110%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0);    opacity: 1; }
  to   { transform: translateX(110%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: ${({ theme }) => theme.zIndex.toast};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 380px;
  width: calc(100vw - 3rem);

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    top: auto;
    bottom: 1.5rem;
    left: 1rem;
    right: 1rem;
    max-width: 100%;
    width: auto;
  }
`;

const typeStyles = {
  success: css`
    border-left: 4px solid ${({ theme }) => theme.colors.success};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.bgCard},
      rgba(76, 175, 128, 0.08)
    );
    .toast-icon {
      color: ${({ theme }) => theme.colors.success};
    }
  `,
  error: css`
    border-left: 4px solid ${({ theme }) => theme.colors.error};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.bgCard},
      rgba(224, 92, 92, 0.08)
    );
    .toast-icon {
      color: ${({ theme }) => theme.colors.error};
    }
  `,
  warning: css`
    border-left: 4px solid ${({ theme }) => theme.colors.warning};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.bgCard},
      rgba(224, 168, 58, 0.08)
    );
    .toast-icon {
      color: ${({ theme }) => theme.colors.warning};
    }
  `,
  info: css`
    border-left: 4px solid ${({ theme }) => theme.colors.gold};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.bgCard},
      ${({ theme }) => theme.colors.goldMuted}
    );
    .toast-icon {
      color: ${({ theme }) => theme.colors.gold};
    }
  `,
};

const ToastItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(12px);
  box-shadow: ${({ theme }) => theme.shadows.lg};
  animation: ${({ $exiting }) =>
    $exiting
      ? css`
          ${slideOut} 0.3s ease forwards
        `
      : css`
          ${slideIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)
        `};

  ${({ $type }) => typeStyles[$type] || typeStyles.info}
`;

const ToastIcon = styled.div`
  font-size: 1.2rem;
  margin-top: 1px;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.2rem;
`;

const ToastMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const icons = {
  success: faCheckCircle,
  error: faTimesCircle,
  warning: faExclamationTriangle,
  info: faInfoCircle,
};

const titles = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback(
    ({ type = "info", title, message, duration = 4000 }) => {
      const id = ++idRef.current;
      setToasts((prev) => [
        ...prev,
        { id, type, title: title || titles[type], message, exiting: false },
      ]);

      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
      return id;
    },
    [removeToast],
  );

  const toast = {
    success: (msg, opts = {}) =>
      addToast({ type: "success", message: msg, ...opts }),
    error: (msg, opts = {}) =>
      addToast({ type: "error", message: msg, ...opts }),
    warning: (msg, opts = {}) =>
      addToast({ type: "warning", message: msg, ...opts }),
    info: (msg, opts = {}) => addToast({ type: "info", message: msg, ...opts }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer>
        {toasts.map((t) => (
          <ToastItem key={t.id} $type={t.type} $exiting={t.exiting}>
            <ToastIcon className="toast-icon">
              <FontAwesomeIcon icon={icons[t.type]} />
            </ToastIcon>
            <ToastContent>
              <ToastTitle>{t.title}</ToastTitle>
              {t.message && <ToastMessage>{t.message}</ToastMessage>}
            </ToastContent>
            <ToastClose onClick={() => removeToast(t.id)}>
              <FontAwesomeIcon icon={faTimes} />
            </ToastClose>
          </ToastItem>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};
